import { CAPTURE_MESSAGE, captureTarget } from '@xshop/shared';

// A store the admin's themes screen opens with ?capture=1 draws its own first screen as a picture and posts it to the
// admin origin its address names — the still picture Shopify's current theme card shows instead of a live page.
// Nothing here runs on a shopper's visit: the drawing library is fetched only by a capture.

/** How long the page may take to fill in before it is drawn as it stands. */
const SETTLE_CAP_MS = 15_000;
/** How long the first screen must stay filled in before it is drawn. */
const QUIET_MS = 700;
/** How often the first screen is looked at while it fills in. */
const POLL_MS = 150;

/** What a capture posts back: the picture and the width it was drawn at, or why there is none. */
export type CaptureResult = { type: typeof CAPTURE_MESSAGE; width: number; picture?: Blob; error?: string };

// Reports whether this visit is the admin asking for a picture of the store.
export const isCaptureVisit = (): boolean => captureTarget(window.location.search) !== null;

// Reports whether an element shows at least partly on the first screen.
function onFirstScreen(element: Element): boolean {
  const box = element.getBoundingClientRect();
  return box.bottom > 0 && box.top < window.innerHeight && box.width > 0 && box.height > 0;
}

// Reports whether the first screen is still filling in: the shell's waiting ground, a block still reading, or a picture on its way.
function stillLoading(): boolean {
  if (document.querySelector('.sf-loading')) return true;
  if ([...document.querySelectorAll('[aria-busy="true"]')].some(onFirstScreen)) return true;
  return [...document.images].some((image) => onFirstScreen(image) && !image.complete);
}

// Asks for every picture on the first screen now, since a frame out of sight may never reach a lazy picture's turn.
function loadFirstScreen(): void {
  for (const image of document.images) if (image.loading === 'lazy' && onFirstScreen(image)) image.loading = 'eager';
}

// Waits until the first screen has stayed filled in for a moment, or until the cap, then for its fonts.
async function settle(): Promise<void> {
  const deadline = Date.now() + SETTLE_CAP_MS;
  let quietSince = 0;
  while (Date.now() < deadline && (quietSince === 0 || Date.now() - quietSince < QUIET_MS)) {
    loadFirstScreen();
    quietSince = stillLoading() ? 0 : quietSince || Date.now();
    await new Promise((resolve) => setTimeout(resolve, POLL_MS));
  }
  await document.fonts.ready;
}

// Lets go of the pictures and videos below the first screen that have not arrived: the drawing waits for every one
// of them before it starts, in vain for a lazy one, and none of them is in the picture. Nobody but the camera sees this page.
function releaseBelowFirstScreen(): void {
  for (const media of document.querySelectorAll('img, video')) {
    if (onFirstScreen(media) || (media instanceof HTMLImageElement && media.complete)) continue;
    media.removeAttribute('srcset');
    media.removeAttribute('src');
    media.querySelectorAll('source').forEach((source) => source.remove());
  }
}

/** Past this many elements inside it, a box is a layout rather than a line, and is not measured. */
const LINE_SCAN_LIMIT = 40;

// Lists the boxes of a box's text, one per line each text runs over — never the boxes of the elements around it.
function textLines(element: Element): DOMRect[] {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  const lines: DOMRect[] = [];
  for (let text = walker.nextNode(); text; text = walker.nextNode()) {
    if (!text.textContent?.trim()) continue;
    range.selectNodeContents(text);
    lines.push(...[...range.getClientRects()].filter((box) => box.width > 0));
  }
  return lines;
}

// Reports whether a box's text sits on one line: every piece starts within half a line of the first. Overlap is not
// enough — a headline set tight has its lines' boxes overlapping.
function onOneLine(element: Element): boolean {
  if (element.getElementsByTagName('*').length > LINE_SCAN_LIMIT) return false;
  const [first, ...rest] = textLines(element);
  return Boolean(first) && rest.every((box) => Math.abs(box.top - first.top) < first.height / 2);
}

// Keeps every line of the first screen on one line in the picture, which measures text a hair wider than the page does.
function holdLines(): void {
  for (const element of document.body.querySelectorAll<HTMLElement>('*')) {
    if (onFirstScreen(element) && onOneLine(element)) {
      element.style.whiteSpace = 'nowrap';
      element.style.flexWrap = 'nowrap';
    }
  }
}

// Clears a cloned picture's other sources, whose addresses a drawn page cannot fetch; its own address is drawn inline.
function dropSources(cloned: Node): void {
  if (cloned instanceof HTMLSourceElement) cloned.removeAttribute('srcset');
}

// Reports whether a read the page drew from failed — refused for load or broken on the server — so what it shows is
// the theme's stand-in content rather than the store's, which must never be kept as the store's picture.
function readFailed(): boolean {
  return performance.getEntriesByType('resource').some((entry) => {
    const status = (entry as PerformanceResourceTiming).responseStatus ?? 200;
    return entry.name.includes('/api/v1/') && (status === 429 || status >= 500);
  });
}

// Reads the colour the page is painted on, so a picture's empty corners match it.
function pageColor(): string {
  const painted = [document.body, document.documentElement]
    .map((element) => getComputedStyle(element).backgroundColor)
    .find((color) => color && color !== 'transparent' && !/,\s*0\)$/.test(color));
  return painted ?? '#ffffff';
}

// Draws the first screen as a WebP picture, twice as sharp on a phone, leaving out everything below it.
async function drawFirstScreen(): Promise<Blob> {
  const { domToBlob } = await import('modern-screenshot');
  const { innerWidth: width, innerHeight: height } = window;
  releaseBelowFirstScreen();
  holdLines();
  return domToBlob(document.body, {
    width, height, scale: width < 768 ? 2 : 1, type: 'image/webp', quality: 0.8,
    backgroundColor: pageColor(), timeout: 8_000, onCloneEachNode: dropSources,
    // The drawn page is a body inside a picture, which takes the browser's 8px default margin unless told otherwise.
    style: { margin: '0' },
    // A slideshow shows its first slide by being scrolled to it; without this the picture shows the row unscrolled.
    features: { restoreScrollPosition: true },
    filter: (node) => !(node instanceof Element) || node.getBoundingClientRect().top < height
  });
}

// Draws the first screen once it settles and posts it to the admin that asked; a failure is posted too, so it stops waiting.
export async function captureScreen(): Promise<void> {
  const target = captureTarget(window.location.search);
  // One picture per page: the page marks itself, so a second mount of the shell takes none.
  if (!target || window.parent === window || document.documentElement.dataset.capture) return;
  document.documentElement.dataset.capture = 'taking';
  const width = window.innerWidth;
  try {
    await settle();
    if (readFailed()) throw new Error('The store did not load fully, so no picture was taken.');
    const result: CaptureResult = { type: CAPTURE_MESSAGE, width, picture: await drawFirstScreen() };
    window.parent.postMessage(result, target);
  } catch (error) {
    const result: CaptureResult = { type: CAPTURE_MESSAGE, width, error: error instanceof Error ? error.message : 'The page could not be drawn.' };
    window.parent.postMessage(result, target);
  }
}
